import { AgeLabelType, AgeRangeType, GenderType, PregnantPeriodType } from '@granlupo/api-client/generated/content/zeus'
import {
	BODY_CONDITION_SYSTEM_COEFICIENT,
	BREASTFEEDING_COEFICIENT,
	BREASTFEEDING_HIGH_LITTER_EXTRA_COEFICIENT,
	BREASTFEEDING_HIGH_LITTER_EXTRA_PUPPY_COEFICIENT,
	BREASTFEEDING_LOW_LITTER_EXTRA_COEFICIENT,
	DAILY_ENERGY_REQUIREMENTS_COEFICIENT_DEFAULT,
	DEFINED_DAILY_DOSE_COEFICIENT,
	PREGNANCY_ADVANCED_COEFICIENT,
	PREGNANCY_COEFICIENT,
	PUPPY_ADVANCED_COEFICIENT_ONE,
	PUPPY_ADVANCED_COEFICIENT_TWO,
	PUPPY_COEFICIENT,
} from './digitContants'

export function getDailyEnergyRequirement(
	age: { ageRange?: AgeRangeType; categoryLabel?: AgeLabelType } | undefined,
	bodyType: { value: number } | undefined,
	breed: { dailyEnergyRequirement?: number; averageWeightMale?: number; averageWeightFemale?: number } | undefined,
	breastfeedingCategory: { coeficient: number } | null | undefined,
	activity: { dailyEnergyRequirementCoeficient?: number | undefined } | undefined,
	breastfeedingPuppyAmount: number | null | undefined,
	pregnancyType: PregnantPeriodType | undefined,
	customIdealWeight: number | null | undefined,
	weight: number | null | undefined,
	gender: GenderType | undefined,
) {
	const averageWeightBasedOnGender =
		gender === GenderType.male ? Number(breed?.averageWeightMale) : Number(breed?.averageWeightFemale) // kg

	const adultDog = age?.categoryLabel?.toLowerCase() === AgeLabelType.adult
	const juniorDog = age?.categoryLabel?.toLowerCase() === AgeLabelType.puppy
	const pregnantFemaleDog =
		pregnancyType === PregnantPeriodType.firstHalf || pregnancyType === PregnantPeriodType.secondHalf
	const advancedPregnancy = pregnancyType === PregnantPeriodType.secondHalf // 5. - 9. weeks

	const litterUpToFourPuppies = !!breastfeedingCategory && Number(breastfeedingPuppyAmount) <= 4

	const adultRequiredWeight = Number(customIdealWeight) || getRequiredWeight(Number(weight), bodyType?.value)

	if (juniorDog) {
		if (age?.ageRange === AgeRangeType.puppyNewbornToSeven) {
			const DER = PUPPY_COEFICIENT * Number(weight)

			return DER
		} else {
			const expectedAdultWeight = Number(customIdealWeight) || averageWeightBasedOnGender

			const DER =
				(PUPPY_ADVANCED_COEFICIENT_ONE - PUPPY_ADVANCED_COEFICIENT_TWO * (Number(weight) / expectedAdultWeight)) *
				Math.pow(Number(weight), DAILY_ENERGY_REQUIREMENTS_COEFICIENT_DEFAULT)
			return DER
		}
	}

	if (!!breastfeedingCategory) {
		const extraOperationDependingOnLitterSize = litterUpToFourPuppies
			? BREASTFEEDING_LOW_LITTER_EXTRA_COEFICIENT * Number(breastfeedingPuppyAmount)
			: BREASTFEEDING_HIGH_LITTER_EXTRA_COEFICIENT +
				BREASTFEEDING_HIGH_LITTER_EXTRA_PUPPY_COEFICIENT * Number(breastfeedingPuppyAmount)

		const DER =
			BREASTFEEDING_COEFICIENT * Math.pow(adultRequiredWeight, DAILY_ENERGY_REQUIREMENTS_COEFICIENT_DEFAULT) +
			extraOperationDependingOnLitterSize * adultRequiredWeight * Number(breastfeedingCategory?.coeficient)

		return DER
	}

	if (pregnantFemaleDog) {
		const DER =
			PREGNANCY_COEFICIENT * Math.pow(adultRequiredWeight, DAILY_ENERGY_REQUIREMENTS_COEFICIENT_DEFAULT) +
			(advancedPregnancy ? PREGNANCY_ADVANCED_COEFICIENT * adultRequiredWeight : 0)

		return DER
	}

	if (adultDog) {
		const DER =
			(Number(breed?.dailyEnergyRequirement) === 0
				? Number(activity?.dailyEnergyRequirementCoeficient)
				: Number(breed?.dailyEnergyRequirement)) *
			Math.pow(adultRequiredWeight, DAILY_ENERGY_REQUIREMENTS_COEFICIENT_DEFAULT)

		return DER
	}

	throw new Error('Cannot count DER')
}

export const getRequiredWeight = (weight: number, bodyTypeValue: number | undefined) => {
	const requiredWeight = (weight * 100) / (100 + (Number(bodyTypeValue) - BODY_CONDITION_SYSTEM_COEFICIENT) * 10)

	return Math.floor(requiredWeight * 100) / 100
}

export const getDefinedDailyDoseOfProduct = (der: number, metabolizedEnergy: number) => {
	const getDailyDose = (der * DEFINED_DAILY_DOSE_COEFICIENT) / metabolizedEnergy // kcal/kg

	const roundDailyDose = Math.round(getDailyDose)

	return roundDailyDose // in grams
}

export const getDailyDosePrice = (der: number, dailyDoseCoefficient: number | undefined) => {
	if (dailyDoseCoefficient) {
		return Math.round(der * dailyDoseCoefficient) // musí být round
	}
}

export const getDailyDosePricePerPackage = (
	der: number,
	weight: number,
	metabolizedEnergy: number | undefined,
	price: number | undefined,
) => {
	if (metabolizedEnergy && price) {
		const dailyDoseCofficient = (metabolizedEnergy * weight) / price
		const czkPerMetabolizedEnergy = dailyDoseCofficient >= 0 ? 1 / dailyDoseCofficient : undefined
		return getDailyDosePrice(der, czkPerMetabolizedEnergy)
	}
}
